import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Sparkles, X } from "lucide-react";
import { useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import type {
  DisplayLoyaltyStatus,
  DisplayLoyaltyTier,
  DisplayUncelebratedLoyaltyPromotion,
} from "@/api/orval/model";
import {
  getRetrieveLoyaltyStatusQueryKey,
  useRecordLoyaltyPromotionCelebrated,
} from "@/api/orval/reward/reward";
import { useTranslation } from "@/core/i18n";
import { useHaptics } from "@/modules/haptics";
import { Button } from "atoms/button/Button";

import { LOYALTY_STATUS_PATH } from "./loyaltyPaths";
import { deepTierTone, mixHex, sparkleTierTones } from "./loyaltyTierTone";
import { LoyaltyTierBadge } from "./LoyaltyTierBadge";
import { TierBenefits } from "./TierBenefits";
import type { EnabledLoyaltyStatus } from "./useLoyaltyStatus";

const SPARKLE_COUNT = 28;
const MEDALLION_SCALE = 1.6;
const REVEAL_SECONDS = 0.7;
const BURST_DELAY_SECONDS = 0.35;
const TWINKLE_POSITIONS = [
  "left-[8%] top-[18%]",
  "right-[10%] top-[12%]",
  "left-[2%] top-[62%]",
  "right-[4%] top-[56%]",
] as const;

type LoyaltyUpgradeCelebrationProps = {
  readonly status: EnabledLoyaltyStatus;
};

/**
 * The magic upgrade moment: full screen, the new tier's medallion revealed with a sparkle burst
 * in its colours, its members-only text, and a way to the status page. It shows once per earned
 * promotion: the server is told as soon as it is on screen, and closing it drops it from the
 * cached status so nothing else shows it again.
 */
export const LoyaltyUpgradeCelebration = ({ status }: LoyaltyUpgradeCelebrationProps) => {
  const queryClient = useQueryClient();
  const promotion = status.uncelebratedPromotion;
  const tier = status.currentTier;

  const dismiss = () => {
    queryClient.setQueryData<DisplayLoyaltyStatus>(getRetrieveLoyaltyStatusQueryKey(), (cached) =>
      cached ? { ...cached, uncelebratedPromotion: undefined } : cached,
    );
  };

  return createPortal(
    <AnimatePresence>
      {promotion && tier && (
        <CelebrationOverlay
          key={promotion.loyaltyStatusId}
          promotion={promotion}
          tier={tier}
          topTierIsLifetime={status.isTopTier && status.rules.topTierIsLifetime}
          onDismiss={dismiss}
        />
      )}
    </AnimatePresence>,
    document.body,
  );
};

type CelebrationOverlayProps = {
  readonly promotion: DisplayUncelebratedLoyaltyPromotion;
  readonly tier: DisplayLoyaltyTier;
  readonly topTierIsLifetime: boolean;
  readonly onDismiss: () => void;
};

const CelebrationOverlay = ({
  promotion,
  tier,
  topTierIsLifetime,
  onDismiss,
}: CelebrationOverlayProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { triggerHaptic } = useHaptics();
  const prefersReducedMotion = useReducedMotion() ?? false;
  const { mutate: recordCelebrated } = useRecordLoyaltyPromotionCelebrated();
  const deepTone = deepTierTone(tier.colorHex);

  // Told the moment it is on screen (the overlay mounting), so leaving the app mid-celebration
  // never shows it twice. A new promotion mounts a new overlay (it is keyed by the status).
  const acknowledgeOnDisplay = useCallback(
    (overlay: HTMLDivElement | null) => {
      if (!overlay) return;
      recordCelebrated({ loyaltyStatusId: promotion.loyaltyStatusId });
      void triggerHaptic("reward").catch(() => undefined);
    },
    [promotion.loyaltyStatusId, recordCelebrated, triggerHaptic],
  );

  const viewStatus = () => {
    onDismiss();
    navigate(LOYALTY_STATUS_PATH);
  };

  const title = topTierIsLifetime
    ? t("loyalty.celebrationTitleForLife", { tier: tier.name })
    : t("loyalty.celebrationTitle", { tier: tier.name });
  const hasBenefits = !!tier.membersOnlyDescription || !!tier.description;

  return (
    <motion.div
      ref={acknowledgeOnDisplay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="loyalty-celebration-title"
      className="fixed inset-0 z-[70] flex flex-col overflow-y-auto text-white"
      style={{
        // Portalled out of the app shell, so it is its own `app` container for `desk:` styles.
        containerType: "inline-size",
        containerName: "app",
        background: `radial-gradient(circle at 50% 30%, ${mixHex(tier.colorHex, "#000000", 0.25)} 0%, ${deepTone} 45%, ${mixHex(deepTone, "#000000", 0.6)} 100%)`,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: prefersReducedMotion ? 0.15 : 0.35 }}
    >
      <div className="flex justify-end px-4 pt-[calc(12px_+_env(safe-area-inset-top))]">
        <button
          type="button"
          aria-label={t("loyalty.celebrationClose")}
          className="rounded-full p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
          onClick={onDismiss}
        >
          <X size={24} />
        </button>
      </div>

      <div className="mx-auto flex w-full max-w-sm flex-1 flex-col items-center px-6 pb-[calc(24px_+_env(safe-area-inset-bottom))] text-center desk:justify-center desk:pb-16">
        <div className="relative flex h-60 w-full items-center justify-center">
          <div
            aria-hidden
            className="absolute h-40 w-40 rounded-full blur-3xl"
            style={{ backgroundColor: tier.colorHex, opacity: 0.45 }}
          />
          {!prefersReducedMotion && <SparkleBurst colorHex={tier.colorHex} />}
          {!prefersReducedMotion &&
            TWINKLE_POSITIONS.map((position, index) => (
              <motion.div
                key={position}
                aria-hidden
                className={`pointer-events-none absolute text-white/70 ${position}`}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: [0, 1, 0.4, 1], scale: [0.6, 1.15, 0.8, 1], rotate: 90 }}
                transition={{
                  delay: BURST_DELAY_SECONDS + index * 0.2,
                  duration: 2.6,
                  repeat: Infinity,
                  repeatType: "mirror",
                }}
              >
                <Sparkles className="h-5 w-5" />
              </motion.div>
            ))}
          <motion.div
            className="relative"
            initial={
              prefersReducedMotion
                ? { opacity: 0, scale: MEDALLION_SCALE }
                : { opacity: 0, scale: 0.3, rotate: -25 }
            }
            animate={{ opacity: 1, scale: MEDALLION_SCALE, rotate: 0 }}
            transition={
              prefersReducedMotion
                ? { duration: 0.15 }
                : { type: "spring", stiffness: 160, damping: 12, duration: REVEAL_SECONDS }
            }
          >
            <LoyaltyTierBadge tier={tier} size="lg" />
            {!prefersReducedMotion && (
              <div aria-hidden className="absolute inset-0 overflow-hidden rounded-full">
                <motion.div
                  className="absolute inset-y-0 w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                  initial={{ x: "-150%" }}
                  animate={{ x: "250%" }}
                  transition={{ delay: REVEAL_SECONDS, duration: 0.9, ease: "easeInOut" }}
                />
              </div>
            )}
          </motion.div>
        </div>

        <motion.div
          className="flex w-full flex-col items-center gap-2"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: prefersReducedMotion ? 0 : REVEAL_SECONDS * 0.8, duration: 0.4 }}
        >
          <p className="text-body3 font-semibold uppercase tracking-[0.16em] text-white/75">
            {t("loyalty.celebrationEyebrow")}
          </p>
          <h2 id="loyalty-celebration-title" className="text-[32px] font-semibold leading-tight">
            {title}
          </h2>
        </motion.div>

        {hasBenefits && (
          <motion.section
            aria-label={t("loyalty.membersOnlyLabel", { tier: tier.name })}
            className="mt-6 flex w-full flex-col gap-2 rounded-2xl border border-white/15 bg-white/10 p-4 text-left backdrop-blur-sm"
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: prefersReducedMotion ? 0 : REVEAL_SECONDS * 1.3, duration: 0.45 }}
          >
            <p className="flex items-center gap-1.5 text-body3 font-semibold uppercase tracking-[0.12em] text-white/75">
              <Sparkles aria-hidden className="h-3.5 w-3.5" />
              {t("loyalty.membersOnlyLabel", { tier: tier.name })}
            </p>
            <TierBenefits tier={tier} tone="onDark" />
          </motion.section>
        )}

        <div className="grow desk:hidden" />

        <div className="mt-8 flex w-full flex-col gap-2 desk:mt-10">
          <Button fullWidth style="white" onClick={viewStatus}>
            {t("loyalty.celebrationViewStatus")}
          </Button>
          <button
            type="button"
            className="py-2 text-body2 font-semibold text-white/80 hover:text-white"
            onClick={onDismiss}
          >
            {t("loyalty.celebrationNotNow")}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

/** Sparkles thrown out from behind the medallion in the tier's colours, once. */
const SparkleBurst = ({ colorHex }: { readonly colorHex: string }) => {
  const tones = sparkleTierTones(colorHex);
  const sparkles = useMemo(
    () =>
      Array.from({ length: SPARKLE_COUNT }, (_, index) => {
        const angle = (index / SPARKLE_COUNT) * Math.PI * 2 + Math.random() * 0.3;
        const distance = 100 + Math.random() * 90;
        return {
          x: Math.cos(angle) * distance,
          y: Math.sin(angle) * distance,
          size: 7 + Math.random() * 9,
          delay: BURST_DELAY_SECONDS + Math.random() * 0.25,
          isStar: index % 3 === 0,
        };
      }),
    [],
  );

  return (
    <div
      aria-hidden
      data-testid="loyalty-sparkle-burst"
      className="pointer-events-none absolute inset-0 flex items-center justify-center"
    >
      {sparkles.map((sparkle, index) => (
        <motion.span
          key={index}
          className="absolute"
          style={{ width: sparkle.size, height: sparkle.size }}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0.2 }}
          animate={{ x: sparkle.x, y: sparkle.y, opacity: [0, 1, 0], scale: [0.2, 1, 0.6] }}
          transition={{ delay: sparkle.delay, duration: 1.5, ease: "easeOut" }}
        >
          {sparkle.isStar ? (
            <svg viewBox="0 0 10 10" className="h-full w-full">
              <path
                d="M5 0 L6.2 3.8 L10 5 L6.2 6.2 L5 10 L3.8 6.2 L0 5 L3.8 3.8 Z"
                fill={tones[index % tones.length]}
              />
            </svg>
          ) : (
            <span
              className="block h-full w-full rounded-full"
              style={{ backgroundColor: tones[index % tones.length] }}
            />
          )}
        </motion.span>
      ))}
    </div>
  );
};
